import { useEffect, useMemo, useRef, useState } from 'react';
import { configurePdfJsWorker } from '../../../utils/reviewablePdf';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function apiHeaders() {
  return {
    apikey: SUPABASE_KEY,
    Authorization: 'Bearer ' + SUPABASE_KEY
  };
}

function currentScale() {
  if (typeof window === 'undefined') return 1;

  if (window.innerWidth < 520) {
    return Math.max(0.45, (window.innerWidth - 44) / 612);
  }

  if (window.innerWidth < 820) return 0.78;
  return 1.05;
}

function nodeElement(node) {
  if (!node) return null;
  return node.nodeType === Node.ELEMENT_NODE
    ? node
    : node.parentElement;
}

function normalisedRects(range, layerElement) {
  const layerRect = layerElement.getBoundingClientRect();

  return Array.from(range.getClientRects())
    .filter(rect => rect.width > 1 && rect.height > 1)
    .map(rect => ({
      x: Math.max(0, (rect.left - layerRect.left) / layerRect.width),
      y: Math.max(0, (rect.top - layerRect.top) / layerRect.height),
      width: Math.min(1, rect.width / layerRect.width),
      height: Math.min(1, rect.height / layerRect.height)
    }))
    .filter(rect =>
      rect.x < 1 &&
      rect.y < 1 &&
      rect.width > 0 &&
      rect.height > 0
    );
}

function PdfPage({
  pdf,
  pdfUtil,
  pageNumber,
  scale,
  comments,
  editable,
  activeCommentId,
  onActivateComment,
  pendingAnchor,
  pendingText,
  setPendingText,
  onCancelPending,
  onSavePending,
  editingCommentId,
  editingText,
  setEditingText,
  onBeginEdit,
  onCancelEdit,
  onSaveEdit,
  onDeleteComment,
  onSelectAnchor,
  onSelectionError
}) {
  const canvasRef = useRef(null);
  const pageLayerRef = useRef(null);
  const textLayerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [renderError, setRenderError] = useState('');

  useEffect(() => {
    let cancelled = false;
    let renderTask = null;

    async function renderPage() {
      try {
        const page = await pdf.getPage(pageNumber);
        if (cancelled) return;

        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        const textLayer = textLayerRef.current;

        if (!canvas || !textLayer) return;

        setDimensions({ width: viewport.width, height: viewport.height });
        setRenderError('');

        const ratio = Math.max(1, window.devicePixelRatio || 1);
        canvas.width = Math.floor(viewport.width * ratio);
        canvas.height = Math.floor(viewport.height * ratio);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        const context = canvas.getContext('2d');
        context.setTransform(ratio, 0, 0, ratio, 0, 0);

        renderTask = page.render({
          canvasContext: context,
          viewport
        });

        const textContent = await page.getTextContent();
        await renderTask.promise;

        if (cancelled) return;

        textLayer.innerHTML = '';
        textLayer.style.width = `${viewport.width}px`;
        textLayer.style.height = `${viewport.height}px`;
        textLayer.dataset.prPage = String(pageNumber);

        (textContent.items || []).forEach(item => {
          if (!item || typeof item.str !== 'string' || !item.str) return;

          const tx = pdfUtil.transform(viewport.transform, item.transform);
          const angle = Math.atan2(tx[1], tx[0]);
          const fontHeight = Math.hypot(tx[2], tx[3]);
          const fontStyle = textContent.styles?.[item.fontName] || {};

          let top = tx[5] - fontHeight;
          if (fontStyle.ascent) {
            top = tx[5] - fontHeight * fontStyle.ascent;
          } else if (fontStyle.descent) {
            top = tx[5] - fontHeight * (1 + fontStyle.descent);
          }

          const span = document.createElement('span');
          span.textContent = item.str;
          span.style.position = 'absolute';
          span.style.left = `${tx[4]}px`;
          span.style.top = `${top}px`;
          span.style.fontSize = `${fontHeight}px`;
          span.style.fontFamily = fontStyle.fontFamily || 'sans-serif';
          span.style.whiteSpace = 'pre';
          span.style.lineHeight = '1';
          span.style.transformOrigin = '0 0';
          span.style.color = 'transparent';
          span.style.cursor = editable ? 'text' : 'default';

          textLayer.appendChild(span);

          const measured = span.getBoundingClientRect().width;
          const targetWidth = Number(item.width || 0) * scale;
          const scaleX = measured > 0 && targetWidth > 0
            ? targetWidth / measured
            : 1;

          span.style.transform =
            `rotate(${angle}rad) scaleX(${scaleX})`;
        });
      } catch (error) {
        if (!cancelled) {
          console.error('Peer Review PDF page render failed:', error);
          setRenderError('This page could not be displayed.');
        }
      }
    }

    renderPage();

    return () => {
      cancelled = true;
      try {
        renderTask?.cancel();
      } catch {
        // Ignore cancellation races.
      }
    };
  }, [pdf, pdfUtil, pageNumber, scale, editable]);

  function handleTextSelection() {
    if (!editable) return;

    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      return;
    }

    const anchorLayer = nodeElement(selection.anchorNode)?.closest(
      '.pr-pdf-text-layer'
    );
    const focusLayer = nodeElement(selection.focusNode)?.closest(
      '.pr-pdf-text-layer'
    );

    if (!anchorLayer || !focusLayer) return;

    if (
      anchorLayer !== textLayerRef.current ||
      focusLayer !== textLayerRef.current
    ) {
      onSelectionError?.('Select text on one page at a time.');
      selection.removeAllRanges();
      return;
    }

    const selectedText = selection.toString().replace(/\s+/g, ' ').trim();
    if (!selectedText) return;

    const range = selection.getRangeAt(0);
    const rects = normalisedRects(range, textLayerRef.current);

    if (rects.length === 0) {
      onSelectionError?.('That text selection could not be anchored.');
      selection.removeAllRanges();
      return;
    }

    onSelectAnchor?.({
      pageNumber,
      selectedText,
      anchorData: { rects }
    });

    selection.removeAllRanges();
  }

  const pageComments = comments.filter(
    comment => Number(comment.page_number) === pageNumber
  );

  const highlights = pageComments.flatMap(comment => {
    const rects = Array.isArray(comment.anchor_data?.rects)
      ? comment.anchor_data.rects
      : [];

    return rects.map((rect, index) => ({
      comment,
      rect,
      index
    }));
  });

  return (
    <div className="pr-page-unit" style={styles.pageUnit}>
      <div style={styles.pageColumn}>
        <div
          ref={pageLayerRef}
          style={{
            ...styles.pageLayer,
            width: dimensions.width || undefined,
            height: dimensions.height || undefined
          }}
        >
          <canvas ref={canvasRef} style={styles.canvas} />

          <div style={styles.highlightLayer} aria-hidden="true">
            {highlights.map(({ comment, rect, index }) => (
              <div
                key={`${comment.id}-${index}`}
                style={{
                  ...styles.highlight,
                  left: `${rect.x * 100}%`,
                  top: `${rect.y * 100}%`,
                  width: `${rect.width * 100}%`,
                  height: `${rect.height * 100}%`,
                  ...(activeCommentId === comment.id
                    ? styles.highlightActive
                    : {})
                }}
              />
            ))}
          </div>

          <div
            ref={textLayerRef}
            className="pr-pdf-text-layer"
            style={styles.textLayer}
            onMouseUp={handleTextSelection}
          />

          {renderError && (
            <div style={styles.pageError}>{renderError}</div>
          )}
        </div>
      </div>

      <aside style={styles.commentRail}>
        <div style={styles.commentRailHeading}>
          Comments — page {pageNumber}
        </div>

        {editable && pendingAnchor?.pageNumber === pageNumber && (
          <div style={styles.commentEditor}>
            <div style={styles.quotedText}>
              “{pendingAnchor.selectedText}”
            </div>
            <textarea
              style={styles.textarea}
              rows={4}
              value={pendingText}
              onChange={event => setPendingText(event.target.value)}
              placeholder="Add a comment..."
              autoFocus
            />
            <div style={styles.editorActions}>
              <button
                type="button"
                style={styles.smallPrimaryBtn}
                disabled={!pendingText.trim()}
                onClick={onSavePending}
              >
                Add comment
              </button>
              <button
                type="button"
                style={styles.smallBtn}
                onClick={onCancelPending}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {pageComments.length === 0 &&
          !(editable && pendingAnchor?.pageNumber === pageNumber) && (
            <div style={styles.noComments}>
              {editable
                ? 'Select text on this page to add a comment.'
                : 'No comments on this page.'}
            </div>
          )}

        {pageComments.map(comment => {
          const editing = editingCommentId === comment.id;

          return (
            <div
              key={comment.id}
              style={{
                ...styles.commentCard,
                ...(activeCommentId === comment.id
                  ? styles.commentCardActive
                  : {})
              }}
              onClick={() => onActivateComment?.(comment.id)}
            >
              <div style={styles.quotedText}>
                “{comment.selected_text}”
              </div>

              {editing ? (
                <>
                  <textarea
                    style={styles.textarea}
                    rows={4}
                    value={editingText}
                    onChange={event => setEditingText(event.target.value)}
                  />
                  <div style={styles.editorActions}>
                    <button
                      type="button"
                      style={styles.smallPrimaryBtn}
                      disabled={!editingText.trim()}
                      onClick={event => {
                        event.stopPropagation();
                        onSaveEdit(comment.id);
                      }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      style={styles.smallBtn}
                      onClick={event => {
                        event.stopPropagation();
                        onCancelEdit();
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={styles.commentText}>{comment.comment_text}</div>
                  {editable && (
                    <div style={styles.editorActions}>
                      <button
                        type="button"
                        style={styles.smallBtn}
                        onClick={event => {
                          event.stopPropagation();
                          onBeginEdit(comment);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        style={styles.deleteBtn}
                        onClick={event => {
                          event.stopPropagation();
                          onDeleteComment(comment);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </aside>
    </div>
  );
}

export default function PeerReviewDocumentViewer({
  allocationId,
  comments = [],
  editable = false,
  onAddComment = null,
  onUpdateComment = null,
  onDeleteComment = null
}) {
  const scrollerRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const pdfUtilRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [scale, setScale] = useState(currentScale);
  const [pendingAnchor, setPendingAnchor] = useState(null);
  const [pendingText, setPendingText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectionError, setSelectionError] = useState('');

  useEffect(() => {
    function handleResize() {
      setScale(currentScale());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let loadingTask = null;
    let loadedPdf = null;

    async function load() {
      setLoading(true);
      setError('');
      setPageIndex(0);
      setPendingAnchor(null);
      setEditingCommentId(null);

      try {
        const {
          getDocument,
          Util
        } = await configurePdfJsWorker();

        pdfUtilRef.current = Util;

        const response = await fetch(
          `${SUPABASE_URL}/functions/v1/peer-review-pdf?allocation_id=${encodeURIComponent(allocationId)}`,
          { headers: apiHeaders() }
        );

        if (!response.ok) {
          const detail = await response.text();
          throw new Error(
            `Could not load the review document (${response.status}): ${detail}`
          );
        }

        const bytes = new Uint8Array(await response.arrayBuffer());
        loadingTask = getDocument({ data: bytes });
        loadedPdf = await loadingTask.promise;

        if (!cancelled) setPdf(loadedPdf);
      } catch (err) {
        console.error('Peer Review document load failed:', err);
        if (!cancelled) {
          setError(err?.message || 'The review document could not be loaded.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (allocationId) load();

    return () => {
      cancelled = true;
      try {
        loadedPdf?.destroy();
        loadingTask?.destroy();
      } catch {
        // Ignore cleanup races.
      }
    };
  }, [allocationId]);

  const pageNumbers = useMemo(
    () => Array.from({ length: pdf?.numPages || 0 }, (_, index) => index + 1),
    [pdf?.numPages]
  );

  function goToPage(nextIndex) {
    if (!scrollerRef.current || pageNumbers.length === 0) return;

    const bounded = Math.max(0, Math.min(pageNumbers.length - 1, nextIndex));
    setPageIndex(bounded);

    scrollerRef.current.scrollTo({
      left: scrollerRef.current.clientWidth * bounded,
      behavior: 'smooth'
    });
  }

  function handleScroll() {
    const element = scrollerRef.current;
    if (!element || element.clientWidth <= 0) return;

    const next = Math.round(element.scrollLeft / element.clientWidth);
    if (next !== pageIndex) setPageIndex(next);
  }

  function handleAnchor(anchor) {
    setPendingAnchor(anchor);
    setPendingText('');
    setSelectionError('');
  }

  async function savePending() {
    if (!pendingAnchor || !pendingText.trim() || !onAddComment) return;

    setSaving(true);
    try {
      await onAddComment({
        pageNumber: pendingAnchor.pageNumber,
        selectedText: pendingAnchor.selectedText,
        anchorData: pendingAnchor.anchorData,
        commentText: pendingText.trim()
      });
      setPendingAnchor(null);
      setPendingText('');
    } finally {
      setSaving(false);
    }
  }

  function beginEdit(comment) {
    setEditingCommentId(comment.id);
    setEditingText(comment.comment_text || '');
    setActiveCommentId(comment.id);
  }

  async function saveEdit(commentId) {
    if (!editingText.trim() || !onUpdateComment) return;

    setSaving(true);
    try {
      await onUpdateComment(commentId, editingText.trim());
      setEditingCommentId(null);
      setEditingText('');
    } finally {
      setSaving(false);
    }
  }

  async function deleteComment(comment) {
    if (!onDeleteComment) return;
    if (!window.confirm('Delete this inline comment?')) return;

    setSaving(true);
    try {
      await onDeleteComment(comment.id);
      if (activeCommentId === comment.id) setActiveCommentId(null);
      if (editingCommentId === comment.id) {
        setEditingCommentId(null);
        setEditingText('');
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={styles.stateBox}>Loading review document...</div>;
  }

  if (error) {
    return <div style={styles.errorBox}>{error}</div>;
  }

  if (!pdf) return null;

  return (
    <div style={styles.wrap}>
      <style>{`
        .pr-pdf-text-layer span::selection {
          background: rgba(75, 141, 211, 0.35);
          color: transparent;
        }
        @media (max-width: 819px) {
          .pr-page-unit {
            grid-template-columns: minmax(0, 1fr) !important;
          }
        }
      `}</style>

      <div style={styles.toolbar}>
        <button
          type="button"
          style={styles.navBtn}
          disabled={pageIndex <= 0}
          onClick={() => goToPage(pageIndex - 1)}
        >
          ← Previous
        </button>

        <div style={styles.pageCounter}>
          Page {pageIndex + 1} of {pageNumbers.length}
        </div>

        <button
          type="button"
          style={styles.navBtn}
          disabled={pageIndex >= pageNumbers.length - 1}
          onClick={() => goToPage(pageIndex + 1)}
        >
          Next →
        </button>
      </div>

      {editable && (
        <div style={styles.helpText}>
          Highlight text in the document to attach a comment. You can attach
          more than one comment to the same or overlapping text.
        </div>
      )}

      {selectionError && (
        <div style={styles.errorBox}>{selectionError}</div>
      )}

      {saving && <div style={styles.savingText}>Saving...</div>}

      <div
        ref={scrollerRef}
        style={styles.scroller}
        onScroll={handleScroll}
      >
        {pageNumbers.map(pageNumber => (
          <PdfPage
            key={pageNumber}
            pdf={pdf}
            pdfUtil={pdfUtilRef.current}
            pageNumber={pageNumber}
            scale={scale}
            comments={comments}
            editable={editable && !saving}
            activeCommentId={activeCommentId}
            onActivateComment={setActiveCommentId}
            pendingAnchor={pendingAnchor}
            pendingText={pendingText}
            setPendingText={setPendingText}
            onCancelPending={() => {
              setPendingAnchor(null);
              setPendingText('');
            }}
            onSavePending={savePending}
            editingCommentId={editingCommentId}
            editingText={editingText}
            setEditingText={setEditingText}
            onBeginEdit={beginEdit}
            onCancelEdit={() => {
              setEditingCommentId(null);
              setEditingText('');
            }}
            onSaveEdit={saveEdit}
            onDeleteComment={deleteComment}
            onSelectAnchor={handleAnchor}
            onSelectionError={setSelectionError}
          />
        ))}
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    marginTop: '14px'
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    marginBottom: '9px'
  },
  navBtn: {
    border: '1px solid #cbd5dc',
    borderRadius: '7px',
    background: 'white',
    padding: '7px 10px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  pageCounter: {
    fontSize: '13px',
    fontWeight: '800',
    color: '#334e68'
  },
  helpText: {
    fontSize: '12px',
    color: '#66788a',
    marginBottom: '9px'
  },
  savingText: {
    fontSize: '12px',
    color: '#66788a',
    marginBottom: '7px'
  },
  scroller: {
    display: 'flex',
    overflowX: 'auto',
    scrollSnapType: 'x mandatory',
    scrollBehavior: 'smooth',
    width: '100%'
  },
  pageUnit: {
    flex: '0 0 100%',
    width: '100%',
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 280px',
    gap: '16px',
    alignItems: 'start',
    scrollSnapAlign: 'start',
    padding: '2px 3px 12px'
  },
  pageColumn: {
    minWidth: 0,
    display: 'flex',
    justifyContent: 'center',
    overflow: 'auto'
  },
  pageLayer: {
    position: 'relative',
    flexShrink: 0,
    background: 'white',
    boxShadow: '0 2px 14px rgba(0,0,0,0.12)'
  },
  canvas: {
    display: 'block',
    position: 'relative',
    zIndex: 1
  },
  highlightLayer: {
    position: 'absolute',
    inset: 0,
    zIndex: 2,
    pointerEvents: 'none'
  },
  highlight: {
    position: 'absolute',
    background: 'rgba(255, 218, 76, 0.48)',
    borderRadius: '2px'
  },
  highlightActive: {
    background: 'rgba(255, 184, 48, 0.72)',
    boxShadow: '0 0 0 1px rgba(168, 104, 0, 0.35)'
  },
  textLayer: {
    position: 'absolute',
    inset: 0,
    zIndex: 3,
    overflow: 'hidden',
    userSelect: 'text'
  },
  pageError: {
    position: 'absolute',
    inset: 0,
    zIndex: 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.9)',
    color: '#9a2c2c'
  },
  commentRail: {
    minWidth: 0,
    borderLeft: '1px solid #e1e7ec',
    paddingLeft: '12px'
  },
  commentRailHeading: {
    fontSize: '12px',
    fontWeight: '800',
    color: '#52616d',
    marginBottom: '9px'
  },
  commentCard: {
    border: '1px solid #d8e0e6',
    borderRadius: '8px',
    padding: '9px',
    marginBottom: '8px',
    background: '#fffdf4',
    cursor: 'pointer'
  },
  commentCardActive: {
    borderColor: '#d39924',
    boxShadow: '0 0 0 1px rgba(211,153,36,0.15)'
  },
  commentEditor: {
    border: '1px solid #9fb7c7',
    borderRadius: '8px',
    padding: '9px',
    marginBottom: '9px',
    background: '#f5f9fc'
  },
  quotedText: {
    fontSize: '11px',
    lineHeight: 1.4,
    color: '#6b7780',
    fontStyle: 'italic',
    marginBottom: '6px',
    maxHeight: '72px',
    overflow: 'hidden'
  },
  commentText: {
    whiteSpace: 'pre-wrap',
    fontSize: '13px',
    lineHeight: 1.5,
    color: '#273746'
  },
  textarea: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #cbd5dc',
    borderRadius: '6px',
    padding: '7px 8px',
    fontFamily: 'inherit',
    fontSize: '13px',
    resize: 'vertical'
  },
  editorActions: {
    display: 'flex',
    gap: '6px',
    marginTop: '7px'
  },
  smallPrimaryBtn: {
    border: '1px solid #215b7d',
    background: '#215b7d',
    color: 'white',
    borderRadius: '6px',
    padding: '5px 8px',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  smallBtn: {
    border: '1px solid #cbd5dc',
    background: 'white',
    color: '#334e68',
    borderRadius: '6px',
    padding: '5px 8px',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  deleteBtn: {
    border: 'none',
    background: 'transparent',
    color: '#a12a2a',
    padding: '5px 4px',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  noComments: {
    fontSize: '12px',
    color: '#8a98a5',
    lineHeight: 1.45
  },
  stateBox: {
    padding: '18px',
    border: '1px solid #e0e6ea',
    borderRadius: '8px',
    color: '#66788a'
  },
  errorBox: {
    margin: '8px 0',
    padding: '9px 11px',
    border: '1px solid #efb7b7',
    borderRadius: '7px',
    background: '#fff4f4',
    color: '#912d2d',
    fontSize: '12px'
  }
};